<script setup lang="ts">
    import { IInteractionTranscriptionSegment } from '@/js/services/api/interaction/types';
    import { computed } from 'vue';
    import { sentimentCssVariable } from '@/js/composables/useSentimentColor';

    type ParsedSegment = IInteractionTranscriptionSegment & {
        gridColumn: string;
    };

    const props = defineProps<{
        segments: IInteractionTranscriptionSegment[];
        duration: number;
        currentTime: number;
    }>();

    /**
     * Grid tracks alternate silence (unbounded, shrinks freely) and segment
     * (floored at 1px) so a short segment stays visible without overlapping
     * its neighbor.
     */
    const gridTemplateColumns = computed(() => {
        const tracks: string[] = [];
        let cursor = 0;

        for (const segment of props.segments) {
            const silence = Math.max(0, segment.segmentStart - cursor);
            tracks.push(`${silence}fr`);

            const length = Math.max(0, segment.segmentEnd - segment.segmentStart);
            tracks.push(`minmax(1px, ${length}fr)`);

            cursor = segment.segmentEnd;
        }

        tracks.push(`${Math.max(0, props.duration - cursor)}fr`);

        return tracks.join(' ');
    });

    const parsedSegments = computed((): ParsedSegment[] => {
        return props.segments.map((segment, index) => ({
            ...segment,
            // Tracks run [silence, segment, silence, segment, ...] starting with
            // silence, so segment i sits on track 2i + 2 (1-indexed grid lines).
            gridColumn: `${2 * index + 2} / span 1`,
        }));
    });

    // A segment switches to full color as soon as the playhead reaches its
    // start, so the one currently playing is clearly marked, not just past ones.
    const segmentStyle = (segment: ParsedSegment) => {
        const cssVariable = sentimentCssVariable(segment.segmentSentiment);
        const isPlayed = segment.segmentStart <= props.currentTime;

        return {
            gridColumn: segment.gridColumn,
            backgroundColor: isPlayed
                ? `var(${cssVariable})`
                : `color-mix(in srgb, var(${cssVariable}) 45%, white)`,
        };
    };
</script>

<template>
    <div
        class="grid w-full h-4 rounded bg-gray-extralight gap-x-px"
        :style="{ gridTemplateColumns }"
    >
        <span
            v-for="segment in parsedSegments"
            :key="segment.segmentId"
            class="block h-full rounded-[3px]"
            :style="segmentStyle(segment)"
        />
    </div>
</template>
